import { DependencyList, useCallback, useRef, useState } from 'react';
import { useGlobalLoadingHandler } from 'views/components/organisms/globalLoading/hook';

/**
 * 二度押し防止し処理中にローディングを行う関数を作成する
 */
const useOnceSubmit = <T>(
  param: {
    onPreviousSubmit?: (args: T) => Promise<boolean>;
    onSubmit: (args: T) => Promise<void>;
    isLoadingForceCenter?: boolean; // サイドメニューの分だけ右にずらしているので無視して画面中央にローディングを表示するか
    waitMilliSecondsForHideLoading?: number; // ローディングを非表示にするまで待つ時間(ミリ秒)
  },
  deps: DependencyList,
): ((args: T) => Promise<void>) => {
  const submittingRef = useRef(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();
  const { showLoading, hideLoading, setLoadingCenter } = useGlobalLoadingHandler();

  const callback = useCallback(
    async (args: T) => {
      try {
        if (submittingRef.current) return;
        submittingRef.current = true;

        if (param.onPreviousSubmit) {
          if (!(await param.onPreviousSubmit(args))) {
            return;
          }
        }

        setLoadingCenter(param.isLoadingForceCenter === true);
        showLoading();

        await param.onSubmit(args);
      } finally {
        // 少し経過してからfalseにする 連打防止
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = setTimeout(() => {
          submittingRef.current = false;
          hideLoading();
        }, param.waitMilliSecondsForHideLoading ?? 500);
      }
    },
    [...deps, param.onPreviousSubmit, param.onSubmit, showLoading, hideLoading],
  );

  return callback;
};

export default useOnceSubmit;
